import { makeObservable, observable, action } from 'mobx';
import { GridSortModel, GridColumns } from '@mui/x-data-grid';
import { RootStore } from './Root.store';
import { ISortModel } from '@core/interfaces/ISortModel';

export interface IColumnVisibilityModel {
    [key: string]: boolean;
}

export enum reports {
    currentState = 'currentState',
    telemetry = 'telemetry',
}

export type report = `${reports}`;

export type IRow = {
    id: number;
    [key: string]: string | number | null;
};

export interface IColumn {
    field: string;
    headerName: string;
    width: number;
    minWidth: number;
    flex?: number;
    editable?: boolean;
    sortable?: boolean;
    hideable?: boolean;
    filterable?: boolean;
    sortComparator?: (v1: unknown, v2: unknown) => number;
}

export interface IReport {
    rows: IRow[];
    columns: IColumn[];
}

export default class ReportsStore {
    @observable
    public reports: report[] = Object.values(reports);

    @observable
    public report: report;

    @observable
    public columnVisibilityModel: IColumnVisibilityModel;

    @observable
    public sortModel: ISortModel[];

    @observable
    public rows: IRow[];

    @observable
    public columns: IColumn[];

    @observable
    public loading: boolean;

    constructor(private rootStore: RootStore) {
        this.setDefault();
        makeObservable(this);
    }

    @action
    setDefault() {
        this.report = 'currentState';
        this.columnVisibilityModel = {};
        this.sortModel = [];
        this.rows = [];
        this.columns = [];
        this.loading = false;
    }

    @action
    setReport(report: string) {
        const _report = reports[report as report];
        if (_report === report) this.report = _report;
    }

    @action
    setColumnVisibilityModel(model: IColumnVisibilityModel) {
        this.columnVisibilityModel = model;
    }

    @action
    setColumnByNames(cols: string[]) {
        this.showAllCol();
        this.resetSortModel();
        this.columnVisibilityModel = cols
            .map((field) => ({ field, val: true }))
            .reduce(
                (res, item) => ({ ...res, [`${item.field}`]: item.val }),
                {},
            );
    }

    @action
    hideCol(field: string) {
        this.columnVisibilityModel = {
            ...this.columnVisibilityModel,
            [field]: false,
        };
    }

    @action
    toggleCol(field: string) {
        this.columnVisibilityModel = {
            ...this.columnVisibilityModel,
            [field]: !this.columnVisibilityModel[field],
        };
    }

    @action
    showAllCol() {
        this.columnVisibilityModel = Object.keys(this.columnVisibilityModel)
            .map((field) => ({ field, val: true }))
            .reduce((res, item) => ({ ...res, [item['field']]: item.val }), {});
    }

    @action
    hideAllCol() {
        this.columnVisibilityModel = Object.keys(this.columnVisibilityModel)
            .map((field) => ({ field, val: false }))
            .reduce((res, item) => ({ ...res, [item['field']]: item.val }), {});
    }

    @action
    setSortModel(sortModel: GridSortModel) {
        this.sortModel = sortModel;
    }

    @action
    resetSortModel() {
        this.sortModel = [];
    }

    @action
    setColumns(columns: IColumn[]) {
        this.columns = columns;
    }

    @action
    resetColumns() {
        this.columns = [];
    }

    @action
    setRows(rows: IRow[]) {
        this.rows = rows;
    }

    @action
    resetRows() {
        this.rows = [];
    }

    @action
    setLoading() {
        this.loading = true;
    }

    @action
    resetLoading() {
        this.loading = false;
    }
}
